import React, { useState, useEffect } from 'react';
import { CouponFormProps, CouponCreateData, CouponUpdateData, CouponDiscountType } from '../../types';
import Button from '../atoms/Button';
import Input from '../atoms/Input';
import './CouponForm.css';

const CouponForm: React.FC<CouponFormProps> = ({
  coupon,
  onSubmit,
  onCancel,
  loading = false
}) => {
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    discountType: 'percentage' as CouponDiscountType,
    discountValue: 0,
    minimumOrderAmount: 0,
    maximumDiscountAmount: 0,
    usageLimit: 0,
    validFrom: new Date().toISOString().split('T')[0],
    validUntil: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form with coupon data if editing
  useEffect(() => {
    if (coupon) {
      setFormData({
        code: coupon.code,
        name: coupon.name,
        description: coupon.description || '',
        discountType: coupon.discountType,
        discountValue: coupon.discountValue,
        minimumOrderAmount: coupon.minimumOrderAmount || 0,
        maximumDiscountAmount: coupon.maximumDiscountAmount || 0,
        usageLimit: coupon.usageLimit || 0,
        validFrom: coupon.validFrom.toISOString().split('T')[0],
        validUntil: coupon.validUntil ? coupon.validUntil.toISOString().split('T')[0] : ''
      });
    }
  }, [coupon]);

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Required fields
    if (!formData.code.trim()) {
      newErrors.code = 'Coupon code is required';
    } else if (formData.code.trim().length < 3) {
      newErrors.code = 'Coupon code must be at least 3 characters';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Coupon name is required';
    }

    if (formData.discountValue <= 0) {
      newErrors.discountValue = 'Discount value must be greater than 0';
    }

    if (formData.discountType === 'percentage' && formData.discountValue > 100) {
      newErrors.discountValue = 'Percentage discount cannot exceed 100%';
    }

    if (formData.minimumOrderAmount < 0) {
      newErrors.minimumOrderAmount = 'Minimum order amount cannot be negative';
    }

    if (formData.maximumDiscountAmount < 0) {
      newErrors.maximumDiscountAmount = 'Maximum discount amount cannot be negative';
    }

    if (formData.usageLimit < 0) {
      newErrors.usageLimit = 'Usage limit cannot be negative';
    }

    // Date validation
    if (formData.validUntil && formData.validFrom) {
      const fromDate = new Date(formData.validFrom);
      const untilDate = new Date(formData.validUntil);
      
      if (untilDate <= fromDate) {
        newErrors.validUntil = 'End date must be after start date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const submitData: any = {
      code: formData.code.trim().toUpperCase(),
      name: formData.name.trim(),
      discountType: formData.discountType,
      discountValue: formData.discountValue,
      validFrom: new Date(formData.validFrom),
      minimumOrderAmount: formData.minimumOrderAmount * 100, // Always include, convert to cents
      maximumDiscountAmount: formData.maximumDiscountAmount * 100, // Always include, convert to cents
      usageLimit: formData.usageLimit // Always include
    };

    // Only include optional fields if they have values
    if (formData.description.trim()) {
      submitData.description = formData.description.trim();
    }
    if (formData.validUntil) {
      submitData.validUntil = new Date(formData.validUntil);
    }

    onSubmit(submitData);
  };

  const formatPrice = (value: number): string => {
    return `€${value.toFixed(2)}`;
  };

  return (
    <div className="coupon-form">
      <form onSubmit={handleSubmit} className="coupon-form__form">
        <div className="coupon-form__header">
          <h2 className="coupon-form__title">
            {coupon ? 'Edit Coupon' : 'Create New Coupon'}
          </h2>
          <p className="coupon-form__description">
            {coupon 
              ? 'Update the coupon details below.'
              : 'Fill in the details to create a new coupon code.'
            }
          </p>
        </div>

        <div className="coupon-form__fields">
          <div className="coupon-form__row">
            <div className="coupon-form__field">
              <Input
                type="text"
                label="Coupon Code *"
                value={formData.code}
                onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
                placeholder="e.g., SAVE20"
                error={errors.code}
                required
                disabled={loading}
              />
            </div>
            <div className="coupon-form__field">
              <Input
                type="text"
                label="Coupon Name *"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="e.g., 20% Off Sale"
                error={errors.name}
                required
                disabled={loading}
              />
            </div>
            <div className="coupon-form__field">
              <label className="coupon-form__label">Discount Type *</label>
              <select
                value={formData.discountType}
                onChange={(e) => handleInputChange('discountType', e.target.value as CouponDiscountType)}
                className="coupon-form__select"
                disabled={loading}
              >
                <option value="percentage">Percentage</option>
                <option value="fixed">Fixed Amount</option>
              </select>
            </div>
          </div>

          <div className="coupon-form__field">
            <Input
              type="textarea"
              label="Description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Optional description for this coupon"
              disabled={loading}
            />
          </div>

          <div className="coupon-form__row">
            <div className="coupon-form__field">
              <Input
                type="number"
                label={`Discount Value * (${formData.discountType === 'percentage' ? '%' : '€'})`}
                value={formData.discountValue}
                onChange={(e) => handleInputChange('discountValue', parseFloat(e.target.value) || 0)}
                placeholder={formData.discountType === 'percentage' ? '20' : '10.00'}
                error={errors.discountValue}
                required
                disabled={loading}
                min={0}
                step={formData.discountType === 'percentage' ? 1 : 0.01}
              />
            </div>
            <div className="coupon-form__field">
              <Input
                type="number"
                label="Minimum Order Amount (€)"
                value={formData.minimumOrderAmount}
                onChange={(e) => handleInputChange('minimumOrderAmount', parseFloat(e.target.value) || 0)}
                placeholder="0.00"
                error={errors.minimumOrderAmount}
                disabled={loading}
                min={0}
                step={0.01}
              />
            </div>
            {formData.discountType === 'percentage' && (
              <div className="coupon-form__field">
                <Input
                  type="number"
                  label="Maximum Discount Amount (€)"
                  value={formData.maximumDiscountAmount}
                  onChange={(e) => handleInputChange('maximumDiscountAmount', parseFloat(e.target.value) || 0)}
                  placeholder="0.00"
                  error={errors.maximumDiscountAmount}
                  disabled={loading}
                  min={0}
                  step={0.01}
                />
              </div>
            )}
          </div>

          <div className="coupon-form__row">
            <div className="coupon-form__field">
              <Input
                type="number"
                label="Usage Limit"
                value={formData.usageLimit}
                onChange={(e) => handleInputChange('usageLimit', parseInt(e.target.value) || 0)}
                placeholder="0 (unlimited)"
                error={errors.usageLimit}
                disabled={loading}
                min={0}
              />
            </div>
            <div className="coupon-form__field">
              <label className="coupon-form__label">Valid From *</label>
              <input
                type="date"
                value={formData.validFrom}
                onChange={(e) => handleInputChange('validFrom', e.target.value)}
                required
                disabled={loading}
                className="coupon-form__date-input"
              />
            </div>
            <div className="coupon-form__field">
              <label className="coupon-form__label">Valid Until</label>
              <input
                type="date"
                value={formData.validUntil}
                onChange={(e) => handleInputChange('validUntil', e.target.value)}
                disabled={loading}
                className="coupon-form__date-input"
              />
              {errors.validUntil && (
                <div className="coupon-form__field-error">{errors.validUntil}</div>
              )}
            </div>
          </div>
        </div>

        <div className="coupon-form__actions">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={loading}
          >
            {loading ? 'Saving...' : (coupon ? 'Update Coupon' : 'Create Coupon')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CouponForm;
